import { useCallback, useRef, useState } from 'react'
import { useStickyPin, useStickyEscape } from './stickyPin'
import { remeasureSbpAnchors } from './anchorBus'

/**
 * Refine in-place sticky (#1092): React port of the toolkit module's
 * initSticky() REFINE branch (searchbar-pro.js:4771-5029 — v3.67.0
 * pin-in-place + v3.68.1 threshold morph). Where the hero detaches to a
 * viewport-centered pill (heroSticky.ts, #934), the refine bar lives in the
 * results column, so the same detach would read as a sideways teleport.
 * Instead: the sentinel pins the FULL bar in place over the column
 * (position:fixed, left/width copied from the spacer's in-flow box —
 * position:sticky can't engage from the short wrapper the bar renders in),
 * and a separate SCROLL THRESHOLD condenses it to the centered pill
 * COMPRESS_AFTER px past the bar. Un-pin happens at the very top via the
 * sentinel, always on the already-re-expanded full bar — so there is no
 * delayed un-pin dropping the bar from its pinned top to its natural
 * position (the module's v3.68.1 "jump" fix, js:4887-4891).
 *
 * Desktop only at the stylesheet's own (min-width: 933px) gate, same as the
 * hero hook — the arming machinery (media gate, sentinel observer,
 * queued-callback guard) lives in the shared scaffold (#1104, stickyPin.ts);
 * this hook owns the pin geometry, the compression morph, and the
 * armed-lifetime listeners (scroll/resize + the spacer ResizeObserver).
 *
 * The compressed summary is the caller's job (React state via
 * useSearchSummaries), same split as the hero hook.
 *
 * `topOffsetPx` (search.heroStickyTopPx) moves the pin boundary below a host
 * site's fixed header so the bar never slides under it before pinning
 * (#1220 item 3). The 64px compression threshold stays a scroll-distance
 * measure, deliberately separate from that boundary.
 */

/** px scrolled past the bar before it condenses (module js:4892). */
export const REFINE_COMPRESS_AFTER_PX = 64

export function useRefineSticky(enabled: boolean, isInteractionActive?: () => boolean, topOffsetPx = 0) {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const spacerRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)
  const [isCompressed, setIsCompressed] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [spacerHeight, setSpacerHeight] = useState(0)

  // The scroll/resize handlers are bound once per arming and read the live
  // state through refs — re-binding window listeners per state flip would
  // drop scroll events during React's commit.
  const stickyRef = useRef(false)
  const expandedRef = useRef(false)
  // Armed = desktop listeners live (set by the scaffold). The async retry
  // below must re-check it: a media flip to mobile can tear everything down
  // between the failed measure and the retry frame.
  const armedRef = useRef(false)
  const retryRaf = useRef<number | null>(null)

  // Live getter, read through a ref so a per-render arrow from the caller
  // never invalidates the armed listeners below.
  const interactionRef = useRef(isInteractionActive)
  interactionRef.current = isInteractionActive

  // Copy the in-flow column box onto the fixed shell (module positionRefine,
  // js:4876-4885). The spacer is the in-flow proxy: before the pin it is a
  // zero-height block in the exact column, while pinned it also holds the
  // vacated height — its left/width are the column box either way. The
  // concrete px in --hfse-sbp-bar-w gives the width morph a real endpoint
  // (width:100% -> 480px does not interpolate reliably, js:4881-4883).
  // Returns false on unusable geometry so enterSticky never pins blind.
  const positionRefine = useCallback((): boolean => {
    const shell = shellRef.current
    const spacer = spacerRef.current
    if (!shell || !spacer) return false
    const r = spacer.getBoundingClientRect()
    if (!r.width) return false
    shell.style.left = `${Math.round(r.left)}px`
    shell.style.width = `${Math.round(r.width)}px`
    // The morph endpoint is the BAR's width, not the column's: the bar is
    // clamped by --hfse-sbp-max-width (850px default, admin 320-1600) and
    // centered in the column, so writing the column px here would snap a
    // narrower bar to full column width the moment it pins. The var is read
    // computed (it cascades from the container CSS); the 850 fallback
    // mirrors the stylesheet fallback.
    const maxW = parseInt(getComputedStyle(shell).getPropertyValue('--hfse-sbp-max-width'), 10) || 850
    shell.style.setProperty('--hfse-sbp-bar-w', `${Math.min(Math.round(r.width), maxW)}px`)
    return true
  }, [])

  // Condense on the scroll threshold, never on pin, and only while not
  // expanded (module updateRefineCompress, js:4895-4908). Scrolling back
  // within the threshold re-expands IN PLACE before the sentinel un-pins.
  // Frozen while a field/popover is live: the desktop popovers are non-modal
  // and anchored to the bar, so condensing mid-interaction would hide the
  // trigger under the pill and strand the open popover. The caller re-runs
  // this via refresh() when the interaction ends.
  const updateCompress = useCallback(() => {
    if (!stickyRef.current || expandedRef.current) return
    if (interactionRef.current?.()) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    // Distance scrolled past the PIN BOUNDARY (the viewport top, or the host
    // header line when an offset is configured): 64px past the point the
    // bar pinned, in every configuration. Identical at offset 0.
    const past = topOffsetPx - sentinel.getBoundingClientRect().top
    setIsCompressed(past > REFINE_COMPRESS_AFTER_PX)
  }, [topOffsetPx])

  // The pending frame's id, so disarm can cancel it: after an offset-driven
  // rebind an old callback must not run against the new state.
  const scrollRaf = useRef<number | null>(null)
  const onScroll = useCallback(() => {
    if (scrollRaf.current != null) return
    scrollRaf.current = requestAnimationFrame(() => {
      scrollRaf.current = null
      updateCompress()
    })
  }, [updateCompress])

  const commitSticky = useCallback(() => {
    // Measure BEFORE the pin class applies (this render's DOM is still
    // in-flow), same ordering as the module's enterSticky (js:4915-4929).
    const shell = shellRef.current
    if (shell && shell.offsetHeight) setSpacerHeight(shell.offsetHeight)
    stickyRef.current = true
    setIsSticky(true)
    // Condense immediately if the pin lands already scrolled deep (anchor
    // jump / restored scroll position), module js:4929.
    updateCompress()
  }, [updateCompress])

  const enterSticky = useCallback(() => {
    // Never pin blind: a zero-width spacer is a layout race (embed host CSS
    // still landing, display:none ancestor mid-toggle), and a fixed shell
    // with no left/width shrink-to-fits at the wrong place. Geometry first;
    // on failure retry ONCE next frame and only commit if the bar is still
    // scrolled past. A host that stays 0-wide simply stays un-pinned — the
    // next sentinel threshold crossing retries the whole entry.
    if (positionRefine()) {
      commitSticky()
      return
    }
    retryRaf.current = requestAnimationFrame(() => {
      retryRaf.current = null
      // Cancelled logically as well as via cancelAnimationFrame: teardown
      // (unmount OR mobile media flip) must never be followed by a pin.
      if (!armedRef.current || stickyRef.current) return
      const sentinel = sentinelRef.current
      // Same boundary the observer pins on (offset-aware, #1220 item 3).
      if (!sentinel || sentinel.getBoundingClientRect().top >= topOffsetPx) return
      if (positionRefine()) commitSticky()
    })
  }, [positionRefine, commitSticky, topOffsetPx])

  const exitSticky = useCallback(() => {
    stickyRef.current = false
    expandedRef.current = false
    setIsSticky(false)
    setIsCompressed(false)
    setIsExpanded(false)
    setSpacerHeight(0)
    // Clear the imperative pin geometry (module exitSticky, js:4945) so the
    // returned-to-flow shell doesn't keep a stale fixed-position box.
    const shell = shellRef.current
    if (shell) {
      shell.style.left = ''
      shell.style.width = ''
      shell.style.removeProperty('--hfse-sbp-bar-w')
    }
  }, [])

  const expand = useCallback(() => {
    if (!stickyRef.current || expandedRef.current) return
    expandedRef.current = true
    setIsExpanded(true)
    setIsCompressed(false)
  }, [])

  // Collapse returns to the PILL (module collapseSticky, js:4964-4977) —
  // the next scroll tick re-expands in place if within the threshold.
  const collapse = useCallback(() => {
    if (!stickyRef.current || !expandedRef.current) return
    expandedRef.current = false
    setIsExpanded(false)
    setIsCompressed(true)
  }, [])

  // Keep the fixed bar aligned to the column on resize (module js:4998-5000).
  // A successful re-projection also publishes an anchor re-measure (#1259):
  // pinned, the shell sits in the elevated body portal, so the popovers'
  // fallback observer on `bar.closest('.hfse')` watches a display:contents
  // container that never fires — a column shift that moves an already
  // max-width bar WITHOUT resizing it would strand an open popover at its
  // old coordinates. Same on the spacer ResizeObserver below.
  const onResize = useCallback(() => {
    if (stickyRef.current) {
      if (positionRefine()) remeasureSbpAnchors()
      updateCompress()
    }
  }, [positionRefine, updateCompress])

  // Armed-lifetime extras the hero hook doesn't have, mounted by the
  // scaffold's onArm/onDisarm: the scroll/resize listeners and the spacer
  // ResizeObserver. The column can reflow WITHOUT a window resize — the map
  // toggle flips .hfse-body between full width and 55% — so the observer on
  // the spacer (the in-flow column proxy) re-projects pinned geometry, and
  // lets a host that was zero-width at the sentinel crossing pin once it
  // becomes measurable.
  const roRef = useRef<ResizeObserver | null>(null)
  const onArm = useCallback(() => {
    if ('ResizeObserver' in window && spacerRef.current) {
      roRef.current = new ResizeObserver(() => {
        if (!armedRef.current) return
        if (stickyRef.current) {
          if (positionRefine()) remeasureSbpAnchors()
          updateCompress()
        } else {
          const sentinel = sentinelRef.current
          if (sentinel && sentinel.getBoundingClientRect().top < topOffsetPx) enterSticky()
        }
      })
      roRef.current.observe(spacerRef.current)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
  }, [onScroll, onResize, positionRefine, updateCompress, enterSticky, topOffsetPx])

  const onDisarm = useCallback(() => {
    roRef.current?.disconnect()
    roRef.current = null
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onResize)
    if (retryRaf.current != null) {
      cancelAnimationFrame(retryRaf.current)
      retryRaf.current = null
    }
    if (scrollRaf.current != null) {
      cancelAnimationFrame(scrollRaf.current)
      scrollRaf.current = null
    }
  }, [onScroll, onResize])

  useStickyPin(enabled, sentinelRef, armedRef, {
    onEnter: enterSticky,
    onExit: exitSticky,
    onArm,
    onDisarm,
    pinBoundaryPx: topOffsetPx,
  })

  // Esc collapses the expanded bar back to the pill (module js:5010-5014).
  // Layered close: an Esc that a popover consumed (defaultPrevented), or one
  // fired while a field/popover is still live, closes the INNER surface only
  // — the next Esc collapses the bar.
  const escGuard = useCallback(
    (e: KeyboardEvent) => e.defaultPrevented || !!interactionRef.current?.(),
    [],
  )
  useStickyEscape(isSticky && isExpanded, collapse, escGuard)

  return {
    sentinelRef,
    shellRef,
    spacerRef,
    isSticky,
    isCompressed,
    isExpanded,
    spacerHeight,
    expand,
    collapse,
    // Re-evaluate compression after an external suppressor clears (a popover
    // closing between scroll events).
    refresh: updateCompress,
  }
}
